import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from './App';
import API from './api';
import { supabase } from './supabaseClient';
import { FcGoogle } from 'react-icons/fc';
import { FaSteam, FaDiscord, FaFacebook, FaTimes, FaUser, FaLock, FaEnvelope, FaExclamationCircle, FaUserTag, FaSignOutAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, initialView = 'login' }) => {
  const [view, setView] = useState(initialView); // 'login', 'register', or 'forgotPassword'
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();


  const handleGoogleAuth = async () => {
    try {
      setLoading(true);
      setError('');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth/callback'
        }
      });
      if (error) throw error;
    } catch (error) {
      setError('Google auth failed. Please try again.');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await API.post('/auth/login', { username, password });
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        const userData = {
          username: response.data.username,
          balance: response.data.balance,
          id: response.data.id || response.data.userId,
          totalWagered: response.data.totalWagered || 0,
          referralCode: response.data.referralCode,
          avatar: response.data.avatar || '',
          email: response.data.email || '',
          emailVerified: response.data.emailVerified || false,
          referralCodeChanged: response.data.referralCodeChanged || false,
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        onClose();
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await API.post('/auth/register', { 
        username, 
        email, 
        password,
        referredBy: referralCode
      });
      // After registration, switch to login
      setView('login');
      setError('Account created! Please sign in.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await API.post('/auth/forgot-password', { email });
      setError(response.data.message || 'If the email exists, a reset link has been sent.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request password reset.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div 
        className="auth-modal" 
        onClick={e => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        <button className="modal-close" onClick={onClose}><FaTimes /></button>
        
        {/* Left Side: Character & Branding */}
        <div className="modal-left" style={{ backgroundImage: 'url("/bannerimg.png")' }}>
          <div className="modal-left-overlay"></div>
          
          <div className="brand">
             <div className="logo-box">
                <img src="/coinimg.png" alt="Logo" className="logo-img-clash" />
                <span className="logo-text-clash">Rusty<span className="purple">Coin</span></span>
             </div>
          </div>
          
          <div className="terms-notice">
            By accessing this site, I confirm that I am at least 18 years old and have read and agree with the <a href="/tos" style={{ color: 'var(--primary-color)', textDecoration: 'underline', cursor: 'pointer' }}>Terms of Service</a>. I also confirm that I have read the <a href="/privacy" style={{ color: 'var(--primary-color)', textDecoration: 'underline', cursor: 'pointer' }}>Privacy Policy</a>, and fully accept the terms.
          </div>
        </div>

        {/* Right Side: Auth Form */}
        <div className="modal-right">
           <div className="auth-tabs">
              <button 
                className={`tab ${view === 'login' ? 'active' : ''}`}
                onClick={() => { setView('login'); setError(''); }}
              >
                Login
              </button>
              <button 
                className={`tab ${view === 'register' ? 'active' : ''}`}
                onClick={() => { setView('register'); setError(''); }}
              >
                Register
              </button>
           </div>

           <div className="form-container">
              <AnimatePresence mode="wait">
                {view === 'login' ? (
                  <motion.form 
                    key="login"
                    onSubmit={handleLogin}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="form-field">
                       <label>Username</label>
                       <input 
                         type="text" 
                         value={username}
                         onChange={e => setUsername(e.target.value)}
                         placeholder="Enter your username"
                         required
                       />
                    </div>
                    <div className="form-field">
                       <label>Password</label>
                       <input 
                         type="password" 
                         value={password}
                         onChange={e => setPassword(e.target.value)}
                         placeholder="Enter your password"
                         required
                       />
                       <div className="forgot-pwd" onClick={() => { setView('forgotPassword'); setError(''); }} style={{ cursor: 'pointer' }}>
                         Forgot Password?
                       </div>
                    </div>

                    {error && <div className="auth-error-msg"><FaExclamationCircle /> {error}</div>}
                    
                    <button type="submit" className="primary-auth-btn" disabled={loading}>
                       {loading ? 'Authenticating...' : 'Sign in'}
                    </button>
                  </motion.form>
                ) : view === 'register' ? (
                  <motion.form 
                    key="register"
                    onSubmit={handleRegister}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <div className="form-field">
                       <label>Username</label>
                       <input 
                         type="text" 
                         value={username}
                         onChange={e => setUsername(e.target.value)}
                         placeholder="Choose a username"
                         required
                       />
                    </div>
                    <div className="form-field">
                       <label>E-mail</label>
                       <input 
                         type="email" 
                         value={email}
                         onChange={e => setEmail(e.target.value)}
                         placeholder="Enter your email"
                         required
                       />
                    </div>
                    <div className="form-field">
                       <label>Password</label>
                       <input 
                         type="password" 
                         value={password}
                         onChange={e => setPassword(e.target.value)}
                         placeholder="Create a password"
                         required
                       />
                    </div>
                    <div className="form-field">
                       <label>Referral Code (Optional)</label>
                       <input 
                         type="text" 
                         value={referralCode}
                         onChange={e => setReferralCode(e.target.value)}
                         placeholder="REFERRAL CODE"
                       />
                    </div>

                    {error && <div className="auth-error-msg"><FaExclamationCircle /> {error}</div>}

                    <button type="submit" className="primary-auth-btn" disabled={loading}>
                       {loading ? 'Processing...' : 'Register Account'}
                    </button>
                  </motion.form>
                ) : (
                  <motion.form 
                    key="forgotPassword"
                    onSubmit={handleForgotPassword}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h3 style={{ color: 'white', marginBottom: '15px' }}>Reset Password</h3>
                    <p style={{ color: '#8a8aa5', marginBottom: '20px', fontSize: '14px' }}>
                      Enter your email address and we'll send you a link to reset your password.
                    </p>
                    
                    <div className="form-field">
                       <label>E-mail</label>
                       <input 
                         type="email" 
                         value={email}
                         onChange={e => setEmail(e.target.value)}
                         placeholder="Enter your email"
                         required
                       />
                    </div>

                    {error && <div className="auth-error-msg" style={{ backgroundColor: error.includes('sent') ? 'rgba(76, 217, 100, 0.1)' : undefined, color: error.includes('sent') ? '#4cd964' : undefined, border: error.includes('sent') ? '1px solid rgba(76, 217, 100, 0.2)' : undefined }}><FaExclamationCircle /> {error}</div>}

                    <button type="submit" className="primary-auth-btn" disabled={loading}>
                       {loading ? 'Processing...' : 'Send Reset Link'}
                    </button>

                    <div className="forgot-pwd" onClick={() => { setView('login'); setError(''); }} style={{ marginTop: '15px', cursor: 'pointer', textAlign: 'center' }}>
                      Back to Login
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              {view !== 'forgotPassword' && (
                <>
                  <div className="auth-footer-divider"></div>
                  <div className="social-login-section">
                     <div className="social-btn-wrapped disabled">
                        <button className="steam-btn auth-wide" type="button">
                           <FaSteam /> Sign in with Steam
                        </button>
                     </div>
                     
                     <div className="social-icons-row">
                        <button className="icon-btn-clash" onClick={handleGoogleAuth} type="button"><FcGoogle /></button>
                        
                        <div className="social-btn-wrapped disabled">
                           <button className="icon-btn-clash" type="button"><FaDiscord style={{color: '#5865F2'}} /></button>
                        </div>

                        <div className="social-btn-wrapped disabled">
                           <button className="icon-btn-clash" type="button"><FaFacebook style={{color: '#1877F2'}} /></button>
                        </div>

                        <div className="social-btn-wrapped disabled">
                           <button className="icon-btn-clash x-icon" type="button">X</button>
                        </div>
                     </div>
                  </div>
                </>
              )}


           </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthModal;
